import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import React, { useEffect } from 'react';
import { Box, Chip, CircularProgress, Paper, Skeleton, Typography } from '@mui/material';
import { useIsMobile } from '../hooks';
import { useI18N } from '../i18n';
import { useAuth } from '../redux/slices/auth';
import { useTheme } from '../redux/slices/theme';
import type { TicketCommentData } from '../redux/slices/comments';
import { useDispatch } from 'react-redux';
import { Attachment, getAttachmentsByCommentId, useAttachments } from '../redux/slices/attachments';

export interface TicketCommentProps {
	comment: TicketCommentData | null;
	handleDownloadAttachment?: (attachment: Attachment, i: number) => void;
	isDownloadingAttachment?: boolean[];
}

export const TicketComment = ({
	comment,
	handleDownloadAttachment,
	isDownloadingAttachment,
}: TicketCommentProps): JSX.Element => {
	const i18n = useI18N();
	const isMobile = useIsMobile();
	const { user } = useAuth();
	const { language } = useTheme();
	const dispatch = useDispatch();

	const { attachmentByCommentId, loading: loadingAttachments } = useAttachments();
	const commentAttachments = comment?.id ? attachmentByCommentId[comment.id] ?? [] : [];

	useEffect(() => {
		if (comment?.id) {
			dispatch(getAttachmentsByCommentId(comment?.id || ''));
		}
	}, [comment?.id, dispatch]);

	return (
		<>
			<Paper
				elevation={6}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					width: 1,
					overflow: 'hidden',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						p: 1,
						backgroundColor: ({ palette }) => palette.info.main,
						color: ({ palette }) => palette.getContrastText(palette.info.main),
					}}
				>
					<Typography variant="caption" noWrap>
						{comment ? (
							<>
								<Box
									component="span"
									sx={{
										fontWeight: 'bold',
									}}
								>
									{new Intl.DateTimeFormat(language, {
										day: '2-digit',
										month: '2-digit',
										year: 'numeric',
										hour: '2-digit',
										hour12: false,
										minute: '2-digit',
										second: '2-digit',
									}).format(new Date(comment.date ?? 0))}
								</Box>{' '}
								{i18n('tickets.view.by')}{' '}
								<Box
									component="span"
									sx={{
										fontWeight: 'bold',
									}}
								>
									{!comment.user ? `${user?.name ?? '-'}` : comment.user?.name}
								</Box>
							</>
						) : (
							<Skeleton variant="text" width={150} />
						)}
					</Typography>
				</Box>

				{comment ? (
					<Box
						dangerouslySetInnerHTML={{
							__html: comment.content,
						}}
						sx={{
							p: isMobile ? 1 : 2,
							whiteSpace: 'pre-wrap',
							wordBreak: 'break-all',
							lineHeight: 'normal',

							'*': {
								margin: 0,
							},
						}}
					/>
				) : (
					<Box
						sx={{
							p: isMobile ? 1 : 2,
							whiteSpace: 'pre-wrap',
							wordBreak: 'break-all',
							lineHeight: 'normal',

							'*': {
								margin: 0,
							},
						}}
					>
						<Skeleton variant="text" width={isMobile ? 250 : 500} />
						<Skeleton variant="text" width={isMobile ? 150 : 300} />
						<Skeleton variant="text" width={isMobile ? 200 : 400} />
						<Skeleton variant="text" width={isMobile ? 100 : 200} />
						<Skeleton variant="text" width={isMobile ? 50 : 100} />
					</Box>
				)}
			</Paper>
			{loadingAttachments ? (
				new Array(1).fill(null).map((commentAttachments, i) => (
					<Chip
						key={i}
						label={
							<Typography variant="inherit">
								<Skeleton variant="text" width={100} />
							</Typography>
						}
						icon={<AttachFileIcon />}
						deleteIcon={<CloudDownloadIcon />}
						onDelete={() => {
							// Do nothing
						}}
					/>
				))
			) : commentAttachments.length > 0 ? (
				commentAttachments.map((attachment, i) => (
					<Chip
						key={attachment.id}
						label={
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}}
							>
								<Typography variant="inherit" noWrap>
									{attachment.name}
								</Typography>

								<Typography variant="inherit" fontWeight="bold">
									({attachment.humanReadableLength})
								</Typography>
							</Box>
						}
						icon={<AttachFileIcon />}
						deleteIcon={
							isDownloadingAttachment && isDownloadingAttachment[i] ? (
								<CircularProgress color="inherit" size="1rem" />
							) : (
								<CloudDownloadIcon />
							)
						}
						disabled={isDownloadingAttachment && isDownloadingAttachment[i]}
						sx={{
							width: 'unset',
							maxWidth: isMobile ? 250 : 300,
						}}
						onDelete={() => handleDownloadAttachment && handleDownloadAttachment(attachment, i)}
					/>
				))
			) : (
				<></>
			)}
		</>
	);
};
